import { isDemoMode } from "@/lib/runtimeMode";
import type { CrmDataProvider } from "../providers/types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { WorkAction, WorkFile, WorkProject } from "./model";
import { storeDemoFile, readDemoFile } from "./demoFiles";
type WorkProvider = CrmDataProvider & {
  workProjectAction(
    id: number,
    revision: number,
    action: WorkAction,
  ): Promise<WorkProject>;
  workProjectManagers(): Promise<{ id: number; name: string }[]>;
};
const demo = isDemoMode();
export async function listWork(
  provider: CrmDataProvider,
): Promise<WorkProject[]> {
  if (demo)
    return (
      await provider.getList<WorkProject>("work_projects", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "DESC" },
        filter: {},
      })
    ).data;
  const { data, error } = await getSupabaseClient()
    .from("work_project_summaries")
    .select("*")
    .order("id", { ascending: false });
  if (error) throw error;
  return data as WorkProject[];
}
export async function getWork(
  provider: CrmDataProvider,
  id: number,
): Promise<WorkProject | null> {
  if (demo)
    return (await provider.getOne<WorkProject>("work_projects", { id })).data;
  const { data, error } = await getSupabaseClient()
    .from("work_projects")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data as WorkProject | null;
}
export async function workAction(
  provider: CrmDataProvider,
  p: WorkProject,
  action: WorkAction,
): Promise<WorkProject> {
  if (demo)
    return (provider as WorkProvider).workProjectAction(
      p.id,
      p.revision,
      action,
    );
  const { data, error } = await getSupabaseClient().rpc("act_work_project", {
    p_id: p.id,
    p_revision: p.revision,
    p_action: action,
  });
  if (error) throw error;
  return data as WorkProject;
}
export async function projectManagers(
  provider: CrmDataProvider,
): Promise<{ id: number; name: string }[]> {
  if (demo) return (provider as WorkProvider).workProjectManagers();
  const { data, error } = await getSupabaseClient().rpc(
    "work_project_managers",
  );
  if (error) throw error;
  return data;
}
export async function uploadWorkFiles(
  p: WorkProject,
  files: File[],
): Promise<WorkFile[]> {
  if (files.length > 10 || files.some((f) => f.size > 20 * 1024 * 1024))
    throw new Error("До 10 файлов по 20 МБ за одно добавление");
  const result: WorkFile[] = [];
  for (const file of files) {
    const type = file.type || "application/octet-stream";
    if (demo) {
      const path = await storeDemoFile(file);
      result.push({ path, name: file.name, type, size: file.size });
    } else {
      const path = `${p.id}/${crypto.randomUUID()}/${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await getSupabaseClient()
        .storage.from("project-work-files")
        .upload(path, file, { upsert: false, contentType: type });
      if (error) throw error;
      result.push({ path, name: file.name, type, size: file.size });
    }
  }
  return result;
}
export async function openWorkFile(file: WorkFile) {
  if (demo) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await readDemoFile(file.path));
    a.download = file.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    return;
  }
  const { data, error } = await getSupabaseClient()
    .storage.from("project-work-files")
    .createSignedUrl(file.path, 60, { download: file.name });
  if (error) throw error;
  const link = document.createElement("a");
  link.href = data.signedUrl;
  link.download = file.name;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.click();
}
export async function workFileSource(file: WorkFile) {
  if (demo) return URL.createObjectURL(await readDemoFile(file.path));
  const { data, error } = await getSupabaseClient()
    .storage.from("project-work-files")
    .createSignedUrl(file.path, 60);
  if (error) throw error;
  return data.signedUrl;
}
